import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './MatchScheduler.css';
import bishopGamesLogo from '../img/bishop_games_logo.png';

const MatchScheduler = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [teams, setTeams] = useState([]);
  const [sports, setSports] = useState([]);
  const [formData, setFormData] = useState({
    tournamentId: '215f8af9-c786-4b42-8f14-adae8bef13f4',
    sportId: '',
    team1Id: '',
    team2Id: '',
    matchDate: '',
    matchTime: '',
    venue: '',
    matchNumber: '',
    roundName: 'GROUP_STAGE',
    courtNumber: 'Court 1',
    player1AId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    player1BId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    player2AId: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
    player2BId: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ✅ Get token from localStorage
  const getAuthToken = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('Token');
    return token;
  };

  // Fetch teams and sports on component mount
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setErrorMessage('Authentication token not found. Please login again.');
      setFetchingData(false);
      return;
    }
    fetchTeams(token);
    fetchSports();
  }, []);

  const fetchTeams = async (token) => {
    try {
      setFetchingData(true);
      const response = await fetch('https://api.asterickx.com/api/Teams', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTeams(data);
        setErrorMessage('');
      } else if (response.status === 401) {
        setErrorMessage('Session expired. Please login again.');
        // Optionally redirect to login
        // setTimeout(() => navigate('/login'), 2000);
      } else {
        setErrorMessage('Failed to fetch teams. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      setErrorMessage('Error loading teams. Please check your connection.');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchSports = () => {
    // Static sports data from your table
    const sportsData = [
      { id: '5F68C7E0-DA61-4142-94E6-B3F018C5255C', code: 'BADMINTON', name: 'Badminton', scoringType: 'SET' },
      { id: '524FB7B9-D5CF-4291-B878-3A38F57C36FA', code: 'BOX_CRICKET', name: 'Box Cricket', scoringType: 'POINT' },
      { id: 'C22D70AC-F8BD-4916-924F-5D4E479AFA78', code: 'CARROM', name: 'Carrom', scoringType: 'SET' },
      { id: '490509BA-0AE1-46D3-8359-2194BDE803BC', code: 'CHESS', name: 'Chess', scoringType: 'RESULT' },
      { id: 'F0F86724-6315-4918-9C4F-4F4B8C7F230E', code: 'CROQUET', name: 'Croquet', scoringType: 'POINT' },
      { id: 'B382B4E3-004A-49B9-A6A5-9AA3CF1B2447', code: 'FUTSAL', name: 'Futsal', scoringType: 'POINT' },
      { id: '6DB11D9F-C504-4CB6-8A2A-B7B8C66EED25', code: 'SATODIYU', name: 'Satodiyu', scoringType: 'POINT' },
      { id: '7CD6C673-1965-486D-A12F-6FB286648373', code: 'TABLE_TENNIS', name: 'Table Tennis', scoringType: 'SET' },
      { id: 'B5C67FD9-80B3-4FBF-947D-541BDE90A8F7', code: 'TENNIS', name: 'Tennis', scoringType: 'SET' },
      { id: '97B9519C-D408-42C9-BA13-1E2DB680758B', code: 'VOLLEYBALL', name: 'Volleyball', scoringType: 'SET' },
      { id: '4455A1D4-F70E-4055-B9D4-43B9094F66A2', code: 'PICKLEBALL', name: 'Pickle Ball', scoringType: 'SET' }
    ];
    setSports(sportsData);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear messages when user starts typing
    setSuccessMessage('');
    setErrorMessage('');
  };

  const validateForm = () => {
    if (!formData.sportId) {
      setErrorMessage('Please select a sport');
      return false;
    }
    if (!formData.team1Id) {
      setErrorMessage('Please select Team 1');
      return false;
    }
    if (!formData.team2Id) {
      setErrorMessage('Please select Team 2');
      return false;
    }
    if (formData.team1Id === formData.team2Id) {
      setErrorMessage('Team 1 and Team 2 cannot be the same');
      return false;
    }
    if (!formData.matchDate) {
      setErrorMessage('Please select match date');
      return false;
    }
    if (!formData.matchTime) {
      setErrorMessage('Please select match time');
      return false;
    }
    if (!formData.venue) {
      setErrorMessage('Please enter venue');
      return false;
    }
    if (!formData.matchNumber) {
      setErrorMessage('Please enter match number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // ✅ Get token before submitting
    const token = getAuthToken();
    if (!token) {
      setErrorMessage('Authentication token not found. Please login again.');
      return;
    }

    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Combine date and time into ISO format
      const matchDateTime = `${formData.matchDate}T${formData.matchTime}:00`;

      const payload = {
        tournamentId: formData.tournamentId,
        sportId: formData.sportId,
        team1Id: formData.team1Id,
        team2Id: formData.team2Id,
        player1AId: formData.player1AId,
        player1BId: formData.player1BId,
        player2AId: formData.player2AId,
        player2BId: formData.player2BId,
        matchDate: formData.matchDate,
        matchTime: formData.matchTime,
        venue: formData.venue,
        matchNumber: parseInt(formData.matchNumber),
        roundName: formData.roundName,
        courtNumber: formData.courtNumber

      };

      console.log('Submitting payload:', payload); // Debug log

      const response = await fetch(
        'https://api.asterickx.com/api/standby-tournament/schedule',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log('Success response:', responseData); // Debug log
        setSuccessMessage('Match scheduled successfully!');
        // Reset form
        setFormData({
          tournamentId: '215f8af9-c786-4b42-8f14-adae8bef13f4',
          sportId: '',
          team1Id: '',
          team2Id: '',
          matchDate: '',
          matchTime: '',
          venue: '',
          matchNumber: '',
          roundType: 'GROUP_STAGE',
          playerId: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
        });
        // Scroll to top to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (response.status === 401) {
        setErrorMessage('Session expired. Please login again.');
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('Error response:', errorData); // Debug log
        setErrorMessage(errorData?.message || `Failed to schedule match (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('Error scheduling match:', error);
      setErrorMessage('Error scheduling match. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      tournamentId: '215f8af9-c786-4b42-8f14-adae8bef13f4',
      sportId: '',
      team1Id: '',
      team2Id: '',
      matchDate: '',
      matchTime: '',
      venue: '',
      matchNumber: '',
      roundType: 'GROUP_STAGE',
      playerId: '3fa85f64-5717-4562-b3fc-2c963f66afa6'
    });
    setSuccessMessage('');
    setErrorMessage('');
  };

  const handleRefreshTeams = () => {
    const token = getAuthToken();
    if (token) {
      fetchTeams(token);
    } else {
      setErrorMessage('Authentication token not found. Please login again.');
    }
  };

  // Show loading state while fetching initial data
  if (fetchingData) {
    return (
      <div className="match-scheduler-container">
        <div className="scheduler-header">
          <button className="back-btn-scheduler" onClick={() => navigate(-1)}>
            <i className="fas fa-arrow-left me-2"></i>
            Back
          </button>
          <div className="scheduler-header-content">
            <div className="scheduler-logo-container">
              <img 
                src={bishopGamesLogo} 
                alt="Bishop Games Season 6" 
                className="scheduler-logo"
              />
            </div>
            <h1 className="scheduler-title">
              <i className="fas fa-calendar-plus me-2"></i>
              Match Scheduler
            </h1>
            <p className="scheduler-subtitle">Bishop Games 6.0 - Admin Panel</p>
          </div>
        </div>
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="loading-text">Loading teams data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="match-scheduler-container">
      {/* Header */}
      <div className="scheduler-header">
        <button className="back-btn-scheduler" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left me-2"></i>
          Back
        </button>

        <div className="scheduler-header-content">
          <div className="scheduler-logo-container">
            <img 
              src={bishopGamesLogo} 
              alt="Bishop Games Season 6" 
              className="scheduler-logo"
            />
          </div>
          <h1 className="scheduler-title">
            <i className="fas fa-calendar-plus me-2"></i>
            Match Scheduler
          </h1>
          <p className="scheduler-subtitle">Bishop Games 6.0 - Admin Panel</p>
        </div>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="alert-message success-alert">
          <i className="fas fa-check-circle me-2"></i>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert-message error-alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {errorMessage}
          {errorMessage.includes('teams') && (
            <button 
              onClick={handleRefreshTeams} 
              className="btn-refresh-inline"
              type="button"
            >
              <i className="fas fa-sync-alt me-1"></i>
              Retry
            </button>
          )}
        </div>
      )}

      {/* Show message if teams failed to load */}
      {teams.length === 0 && !fetchingData && !errorMessage && (
        <div className="alert-message error-alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          No teams found. Please make sure teams are created first.
          <button 
            onClick={handleRefreshTeams} 
            className="btn-refresh-inline"
            type="button"
          >
            <i className="fas fa-sync-alt me-1"></i>
            Refresh
          </button>
        </div>
      )}

      {/* Scheduler Form */}
      <div className="scheduler-form-container">
        <form onSubmit={handleSubmit} className="scheduler-form">
          
          {/* Sport Selection */}
          <div className="form-section">
            <h3 className="section-heading">
              <i className="fas fa-futbol me-2"></i>
              Sport Selection
            </h3>
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="sportId" className="form-label required">
                  <i className="fas fa-trophy me-2"></i>
                  Select Sport
                </label>
                <select
                  id="sportId"
                  name="sportId"
                  value={formData.sportId}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="">-- Select Sport --</option>
                  {sports.map(sport => (
                    <option key={sport.id} value={sport.id}>
                      {sport.name} ({sport.scoringType})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Team Selection */}
          <div className="form-section">
            <h3 className="section-heading">
              <i className="fas fa-users me-2"></i>
              Team Selection
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="team1Id" className="form-label required">
                  <i className="fas fa-flag me-2"></i>
                  Team 1
                </label>
                <select
                  id="team1Id"
                  name="team1Id"
                  value={formData.team1Id}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                  disabled={teams.length === 0}
                >
                  <option value="">-- Select Team 1 --</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="vs-divider">
                <span className="vs-text">VS</span>
              </div>

              <div className="form-group">
                <label htmlFor="team2Id" className="form-label required">
                  <i className="fas fa-flag me-2"></i>
                  Team 2
                </label>
                <select
                  id="team2Id"
                  name="team2Id"
                  value={formData.team2Id}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                  disabled={teams.length === 0}
                >
                  <option value="">-- Select Team 2 --</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Match Details */}
          <div className="form-section">
            <h3 className="section-heading">
              <i className="fas fa-info-circle me-2"></i>
              Match Details
            </h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="matchDate" className="form-label required">
                  <i className="fas fa-calendar-day me-2"></i>
                  Match Date
                </label>
                <input
                  type="date"
                  id="matchDate"
                  name="matchDate"
                  value={formData.matchDate}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="matchTime" className="form-label required">
                  <i className="fas fa-clock me-2"></i>
                  Match Time
                </label>
                <input
                  type="time"
                  id="matchTime"
                  name="matchTime"
                  value={formData.matchTime}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="venue" className="form-label required">
                  <i className="fas fa-map-marker-alt me-2"></i>
                  Venue
                </label>
                <input
                  type="text"
                  id="venue"
                  name="venue"
                  value={formData.venue}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter venue location"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="matchNumber" className="form-label required">
                  <i className="fas fa-hashtag me-2"></i>
                  Match Number
                </label>
                <input
                  type="number"
                  id="matchNumber"
                  name="matchNumber"
                  value={formData.matchNumber}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="e.g., 1"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="roundType" className="form-label required">
                  <i className="fas fa-layer-group me-2"></i>
                  Round Type
                </label>
                <select
                  id="roundType"
                  name="roundType"
                  value={formData.roundType}
                  onChange={handleInputChange}
                  className="form-select"
                  required
                >
                  <option value="GROUP_STAGE">Group Stage</option>
                  <option value="ROUND_ROBIN">Round Robin</option>
                  <option value="QUARTER_FINAL">Quarter Final</option>
                  <option value="SEMI_FINAL">Semi Final</option>
                  <option value="FINAL">Final</option>
                  <option value="THIRD_PLACE">Third Place</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleReset}
              className="btn-reset"
              disabled={loading}
            >
              <i className="fas fa-redo me-2"></i>
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={loading || teams.length === 0}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Scheduling...
                </>
              ) : (
                <>
                  <i className="fas fa-calendar-check me-2"></i>
                  Schedule Match
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MatchScheduler;